import {
  Archived,
  Dialog,
  Pick,
  DropZone,
  getPlan,
  InvalidInput,
  NotFound,
  readList,
  readPlacesFile,
  takeIn,
  type Plan,
  type ReadTrail,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { rowOf, Rows, type Row } from './ImportRows';
import { useLive } from '../../app/live';

type Tab = 'list' | 'file';

interface Props {
  trip: string;
  onClose: () => void;
  /** The trip as it is after the places were taken in. */
  onDone: (plan: Plan) => void;
}

/** A trail read, whether it is taken, the stop it leaves from and the stop at its end. */
interface TrailChoice {
  trail: ReadTrail;
  /** Its name: the file's for it, else the file's own. */
  name: string;
  take: boolean;
  after: string;
  city: string;
  country: string;
}

/**
 * Places taken into a trip (the Import mockup): a list shared from Google Maps or Naver Map,
 * or a GPX, KML or KMZ file. Each place goes to its stop within 30 km, to a new stop or to none,
 * and a file's trail is a way on foot to a new stop at its end. Nothing is added until asked.
 */
export function ImportDialog({ trip, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('list');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [link, setLink] = useState('');
  // The window starts on the link to paste.
  const first = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [listName, setListName] = useState<string | null>(null);
  const [source, setSource] = useState<'google' | 'naver' | 'file'>('google');
  const [rows, setRows] = useState<Row[]>([]);
  const [file, setFile] = useState<string | null>(null);
  const [trail, setTrail] = useState<TrailChoice | null>(null);

  useEffect(() => {
    let live = true;
    getPlan(trip).then(
      (read) => {
        if (live) setPlan(read);
      },
      () => {
        if (live) setProblem(t('importPlaces.failed'));
      },
    );
    return () => {
      live = false;
    };
  }, [trip, t]);

  const said = (error: unknown, invalid: string) => {
    if (error instanceof InvalidInput) return t(invalid);
    if (error instanceof NotFound) return t('importPlaces.notList');
    if (error instanceof Archived) return t('importPlaces.archived');
    return t('importPlaces.failed');
  };

  const start = (next: Tab) => {
    setTab(next);
    setRows([]);
    setTrail(null);
    setListName(null);
    setFile(null);
    setProblem(null);
  };

  const read = () => {
    setBusy(true);
    setProblem(null);
    readList(trip, link.trim()).then(
      (list) => {
        setBusy(false);
        setListName(list.name);
        setSource(list.source);
        setRows(list.places.map(rowOf));
      },
      (error: unknown) => {
        setBusy(false);
        setProblem(said(error, 'importPlaces.badLink'));
      },
    );
  };

  const pick = (chosen: File) => {
    setBusy(true);
    setProblem(null);
    setFile(chosen.name);
    readPlacesFile(trip, chosen).then(
      (read) => {
        setBusy(false);
        setSource('file');
        setRows(read.places.map(rowOf));
        const at = plan?.stops.at(-1);
        const from =
          read.trail?.start?.stop ?? read.trail?.start?.nearest?.stop ?? at?.id ?? '';
        setTrail(
          read.trail && {
            trail: read.trail,
            name: read.trail.name ?? chosen.name,
            take: true,
            after: from,
            city: read.trail.end?.town?.city ?? '',
            country: read.trail.end?.town?.country ?? at?.country ?? '',
          },
        );
      },
      (error: unknown) => {
        setBusy(false);
        setProblem(said(error, 'importPlaces.badFile'));
      },
    );
  };

  const taken = rows.filter((row) => row.take);
  // The trail, when it is taken and its end has a name.
  const walked = trail?.take === true && trail.city.trim() !== '' ? trail : null;
  const walking = walked !== null;
  const add = () => {
    setBusy(true);
    takeIn(trip, {
      source,
      sourceUrl: source === 'file' ? file : link.trim(),
      places: taken.map(({ place, to }) => ({
        name: place.name,
        address: place.address,
        kind: place.kind,
        lat: place.lat,
        lon: place.lon,
        to,
      })),
      trail: walked && {
        name: walked.name,
        after: walked.after,
        city: walked.city.trim(),
        country: walked.country,
        days: walked.trail.days,
        points: walked.trail.points,
      },
    }).then(
      (made) => {
        setBusy(false);
        onDone(made);
      },
      (error: unknown) => {
        setBusy(false);
        setProblem(said(error, 'importPlaces.failed'));
      },
    );
  };

  const toStops = rows.filter((row) => row.place.near.stop !== null).length;
  const label = walking
    ? taken.length > 0
      ? t('importPlaces.addBoth', { count: taken.length })
      : t('importPlaces.addTrail')
    : t('importPlaces.add', { count: taken.length });
  const bar = (
    <div className="segmented import-tabs" role="tablist" aria-label={t('importPlaces.kinds')}>
      {(['list', 'file'] as const).map((one) => (
        <button
          key={one}
          type="button"
          role="tab"
          id={`import-tab-${one}`}
          aria-selected={tab === one}
          aria-controls="import-panel"
          onClick={() => {
            start(one);
          }}
        >
          {t(`importPlaces.${one}`)}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('importPlaces.title')}
      wide
      bar={bar}
      initialFocus={first}
    >
      <div
        className="dialog-body import-body"
        id="import-panel"
        role="tabpanel"
        aria-labelledby={`import-tab-${tab}`}
      >
        {tab === 'list' ? (
          <form
            className="import-link"
            onSubmit={(event) => {
              event.preventDefault();
              read();
            }}
          >
            <label className="field">
              <span className="field-label">{t('importPlaces.link')}</span>
              <input
                ref={first}
                className="text-input"
                type="url"
                inputMode="url"
                placeholder="https://maps.app.goo.gl/…"
                value={link}
                onChange={(event) => {
                  setLink(event.target.value);
                }}
              />
            </label>
            <button type="submit" className="button" disabled={busy || link.trim() === ''}>
              {busy ? t('importPlaces.reading') : t('importPlaces.read')}
            </button>
          </form>
        ) : (
          <DropZone
            label={t('importPlaces.drop')}
            choose={t('importPlaces.choose')}
            hint={t('importPlaces.dropHint')}
            accept=".gpx,.kml,.kmz,application/gpx+xml,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
            onFile={pick}
          />
        )}
        {problem !== null && (
          <p className="form-failed" role="alert">
            {problem}
          </p>
        )}
        {listName !== null && (
          <p className="import-summary">
            <strong>{t('importPlaces.summary', { name: listName, count: rows.length })}</strong>
            {' · '}
            {t('importPlaces.toStops', { count: toStops })}
            {' · '}
            {t('importPlaces.farOff', { count: rows.length - toStops })}
          </p>
        )}
        {file !== null && !busy && problem === null && (
          <p className="import-summary">
            {t('importPlaces.fileRead', {
              file,
              trails: t('importPlaces.trails', { count: trail ? 1 : 0 }),
              points: t('importPlaces.points', { count: rows.length }),
            })}
          </p>
        )}
        {plan && trail && (
          <fieldset className="import-trail">
            <legend>{t('importPlaces.trail', { name: trail.name })}</legend>
            <p className="quiet">
              {trail.trail.ascent === null
                ? t('importPlaces.trailFacts', {
                    km: trail.trail.km,
                    days: t('trips.days', { count: trail.trail.days }),
                  })
                : t('importPlaces.trailClimb', {
                    km: trail.trail.km,
                    ascent: trail.trail.ascent,
                    days: t('trips.days', { count: trail.trail.days }),
                  })}
            </p>
            <div className="import-trail-row">
              <label className="check">
                <input
                  type="checkbox"
                  checked={trail.take}
                  onChange={() => {
                    setTrail({ ...trail, take: !trail.take });
                  }}
                />
                {t('importPlaces.walk')}
              </label>
              <Pick
                label={t('importPlaces.from')}
                searchable={false}
                value={trail.after}
                options={plan.stops.map((stop) => ({ value: stop.id, label: stop.city }))}
                onChange={(after) => {
                  setTrail({ ...trail, after });
                }}
              />
              <label className="field">
                <span className="field-label">{t('importPlaces.endTown')}</span>
                <input
                  className="text-input"
                  value={trail.city}
                  onChange={(event) => {
                    setTrail({ ...trail, city: event.target.value });
                  }}
                />
              </label>
            </div>
          </fieldset>
        )}
        {plan && rows.length > 0 && (
          <Rows
            plan={plan}
            rows={rows}
            head={tab === 'list' ? t('importPlaces.place') : t('importPlaces.pointInFile')}
            onChange={setRows}
          />
        )}
        <p className="quiet import-note">
          {tab === 'list' ? t('importPlaces.note') : t('importPlaces.nothingYet')}
        </p>
      </div>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={onClose}>
          {t('importPlaces.cancel')}
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={busy || (taken.length === 0 && !walking)}
          onClick={add}
        >
          {label}
        </button>
      </div>
    </Dialog>
  );
}

/** The import window over a trip's page: the page reads the trip again once places are in. */
export function ImportWindow({ trip, onClose }: { trip: string; onClose: () => void }) {
  const live = useLive();
  return (
    <ImportDialog
      trip={trip}
      onClose={onClose}
      onDone={() => {
        live.refresh();
        onClose();
      }}
    />
  );
}
